import { FieldValues, Path, useFormContext } from "react-hook-form";
import { ObrConfiguration, ObrPinGeneration } from "../../OBRSettings";
import * as React from "react";
import { useLanguage } from "../../Language";
import {
  Box,
  FormControl,
  InputLabel,
  TextField,
  Typography,
} from "@mui/material";
import { FormTextField } from "../../FormTextField";

export interface PinFieldProps<T extends FieldValues> {
  obrConfiguration: ObrConfiguration;
  name: Path<T>;
  confirmName: Path<T>;
}

interface ReadOnlyFieldProps {
  label: string;
  value: string;
}
const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({ label, value }) => {
  return (
    <FormControl fullWidth>
      <InputLabel shrink>{label}</InputLabel>
      <Box
        sx={{
          padding: "8px 16px", // Aligns with typical TextField padding
          fontSize: "1rem",
          lineHeight: "1.5",
          color: "rgba(0, 0, 0, 0.87)",
        }}
        role="textbox"
        aria-readonly="true"
      >
        <Typography>{value}</Typography>
      </Box>
    </FormControl>
  );
};

export const PinField = <T extends FieldValues>({
  name,
  confirmName,
  obrConfiguration,
}: PinFieldProps<T>) => {
  const { translate } = useLanguage();
  const { formState, getFieldState, getValues, trigger } =
    useFormContext<T>();
  const { isTouched: isConfirmPINTouched } =
    getFieldState(confirmName, formState);

  if (obrConfiguration.howToGeneratePin === ObrPinGeneration.None) {
    return (
      <>
        <FormTextField<T>
          name={name}
          required
          options={{
            validate: (value: string, formValues: T) => {
              console.log("isConfirmPINTouched", isConfirmPINTouched);
              if (isConfirmPINTouched) {
                trigger(confirmName); // trigger validator on Confirm PIN
              }
              if (
                obrConfiguration.pinLengthRequired &&
                value.length !== obrConfiguration.pinLengthRequired
              ) {
                return translate("pinLengthOutOfRange", {
                  PINLENGTH: obrConfiguration.pinLengthRequired.toString(),
                });
              } else if (
                !obrConfiguration.pinLengthRequired &&
                (value.length < 1 || value.length > 8)
              ) {
                return translate("pinLengthOutOfRange", {
                  PINLENGTH: "1-8",
                });
              }

              return true;
            },
          }}
          helperText={translate("ci_PINLengthHint", {
            PINLENGTH: obrConfiguration.pinLengthRequired
              ? obrConfiguration.pinLengthRequired.toString()
              : "1-8",
          })}
          label={translate("ci_PIN")}
          type="password"
        />
        <FormTextField
          required
          name={confirmName}
          options={{
            validate: (value: string, formValues: T) => {
              console.log("match?", value, getValues(name));
              if (value !== getValues(name)) {
                return translate("newPINConfirmPINMustMatchError");
              }
              return true;
            },
          }}
          label={translate("ci_confirmPIN")}
          type="password"
        />
      </>
    );
  }

  if (obrConfiguration.howToGeneratePin === ObrPinGeneration.Last4OfPhone) {
    return (
      <ReadOnlyField
        label={translate("ci_PIN")}
        value={translate("obrPINWasGeneratedWithPhone")}
      />
    );
  }

  if (obrConfiguration.howToGeneratePin === ObrPinGeneration.Birthday) {
    return (
      <ReadOnlyField
        label={translate("ci_PIN")}
        value={translate("obrPINWasGeneratedWithDOB")}
      />
    );
  }
};
